<template>
	<div class="portfolio-benchmark-widget">
		<div class="portfolio-benchmark-widget__header">
			<h3 class="portfolio-benchmark-widget__title">{{ title }}</h3>
			<themed-tooltip 
				placement="bottom"
				:show-delay="300"
				max-width="300px"
			>
				<button 
					class="portfolio-benchmark-widget__info" 
					aria-label="More information about Portfolio Benchmark"
					@mouseenter="loadTooltip('dashboard-portfoliobenchmark')"
				>
					<svg 
						width="14" 
						height="14" 
						viewBox="0 0 24 24" 
						fill="none" 
						stroke="currentColor" 
						stroke-width="2" 
						stroke-linecap="round" 
						stroke-linejoin="round"
					>
						<circle cx="12" cy="12" r="10"></circle>
						<line x1="12" y1="16" x2="12" y2="12"></line>
						<line x1="12" y1="8" x2="12.01" y2="8"></line>
					</svg>
				</button>
				<template #content>
					<div v-html="getTooltipContent('dashboard-portfoliobenchmark')"></div>
				</template>
			</themed-tooltip>
		</div>
		
		<div class="portfolio-benchmark-widget__content">
			<!-- Percentage first (large) -->
			<div class="portfolio-benchmark-widget__percentage-section">
				<span class="portfolio-benchmark-widget__percentage" :class="benchmarkColorClass">
					{{ benchmarkPercentage }}
				</span>
			</div>
			
			<!-- Chart second (smaller) -->
			<div class="portfolio-benchmark-widget__chart">
				<theme-gauge-chart
					:value="benchmark"
					:min="-100"
					:max="100"
					:green-zone="[-100, -5]"
					:yellow-zone="[-5, 5]"
					:red-zone="[5, 100]"
					:show-labels="true"
				/>
			</div>
			
			<!-- Labels at bottom -->
			<div class="portfolio-benchmark-widget__result">
				<div class="portfolio-benchmark-widget__label">
					{{ benchmarkLabel }}
				</div>
				<div class="portfolio-benchmark-widget__sublabel">
					PORTFOLIO VS MARKET RATE
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ThemedTooltip from '../ThemedTooltip.vue';
import ThemeGaugeChart from '../charts/ThemeGaugeChart.vue';
import axios from 'axios';

export default {
	name: 'PortfolioBenchmarkWidget',
	components: {
		ThemedTooltip,
		ThemeGaugeChart
	},
	props: {
		title: {
			type: String,
			default: 'Portfolio Benchmark to Market'
		},
		benchmark: {
			type: Number,
			required: true
		},
		height: {
			type: String,
			default: '180'
		}
	},
	data() {
		return {
			tooltipCache: {}
		}
	},
	computed: {
		benchmarkPercentage() {
			const sign = this.benchmark > 0 ? '+' : '';
			return `${sign}${Math.round(this.benchmark)}%`;
		},
		benchmarkColorClass() {
			if (this.benchmark > 5) return 'portfolio-benchmark-widget__percentage--over';
			if (this.benchmark < -5) return 'portfolio-benchmark-widget__percentage--under';
			return 'portfolio-benchmark-widget__percentage--neutral';
		},
		benchmarkLabel() {
			// All three branches returned the same string, so the card read
			// "Fair - Near Market" at every value, including +128%.
			if (this.benchmark > 5) return 'Over Market';
			if (this.benchmark < -5) return 'Under Market';
			return 'Fair - Near Market';
		}
	},
	methods: {
		getTooltipContent(identifier) {
			if (this.tooltipCache[identifier]) {
				return this.tooltipCache[identifier];
			}
			return '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>';
		},
		async loadTooltip(identifier) {
			if (!this.tooltipCache[identifier]) {
				this.$set(this.tooltipCache, identifier, '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>');
				
				try {
					const response = await axios.get(`/api/tooltips/identifier/${identifier}`);
					this.$set(this.tooltipCache, identifier, response.data.content || 'Tooltip content not available');
				} catch (err) {
					console.error('Error loading tooltip:', err);
					if (err.response && err.response.status === 404) {
						this.$set(this.tooltipCache, identifier, 'Tooltip content is not yet available for this field.');
					} else {
						this.$set(this.tooltipCache, identifier, 'Failed to load information');
					}
				}
			}
		}
	}
}
</script>
